import type { Priority } from "./dto";

/**
 * The SLA policy: how fast each priority must get a first response and a
 * resolution. Fixed in code by decision (2026-09-21) — five numbers that
 * change rarely, and a one-line PR is the right audit trail for changing
 * them. Settings › SLA displays this table; nothing edits it at runtime.
 */

/** Targets for one priority. `resolutionHours` null means no resolution target. */
export interface SlaTarget {
  firstResponseMinutes: number;
  resolutionHours: number | null;
}

export const SLA_TARGETS: Record<Priority, SlaTarget> = {
  0: { firstResponseMinutes: 15, resolutionHours: 4 },
  1: { firstResponseMinutes: 60, resolutionHours: 24 },
  2: { firstResponseMinutes: 4 * 60, resolutionHours: 3 * 24 },
  3: { firstResponseMinutes: 24 * 60, resolutionHours: 10 * 24 },
  4: { firstResponseMinutes: 3 * 24 * 60, resolutionHours: null },
};

/** Priorities in display order. */
export const SLA_PRIORITIES: Priority[] = [0, 1, 2, 3, 4];

/** "15 min", "1 hour", "4 hours", "1 day", "10 days" — whole units only,
 *  rounded, because these are targets and reasons, not stopwatches. */
export function durationLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? "1 hour" : `${h} hours`;
  const d = Math.round(h / 24);
  return d === 1 ? "1 day" : `${d} days`;
}

/** The policy for one priority as the two labels Settings › SLA shows. */
export function describeSla(priority: Priority): { firstResponse: string; resolution: string } {
  const t = SLA_TARGETS[priority];
  return {
    firstResponse: durationLabel(t.firstResponseMinutes),
    resolution: t.resolutionHours === null ? "No target" : durationLabel(t.resolutionHours * 60),
  };
}
